import { useEffect, useState } from 'react';

import {
  Boton,
  Campo,
  Cargando,
  Encabezado,
  Entrada,
  ErrorCaja,
  Panel,
  Seleccion,
} from '../componentes/ui';
import { ErrorApi, pedir } from '../lib/api';
import { useRecurso } from '../lib/consultas';

/**
 * Los datos del fraccionamiento.
 *
 * Existían en la base desde el primer día y **nunca tuvieron pantalla**:
 * corregir el nombre del condominio o ajustar cuántos días se guardan las fotos
 * exigía entrar a la base de datos. Ese último dato es el que más importa —las
 * fotos de identificación llevan CURP, domicilio y firma— y era justo el que
 * estaba más escondido.
 */

interface Condominio {
  id: string;
  nombre: string;
  slug: string;
  calle: string | null;
  ciudad: string | null;
  municipio: string | null;
  pais: string | null;
  codigoPostal: string | null;
  zonaHoraria: string;
  retencionFotosDias: number;
  exigirTarjetonSinQr: boolean;
  limiteVelocidadCamellon: number;
  limiteVelocidadCalles: number;
}

/** Las de México. Se listan en vez de dejar un campo libre porque una zona mal
 *  escrita corre los horarios de todas las invitaciones sin avisar. */
const ZONAS_HORARIAS = [
  'America/Mexico_City',
  'America/Tijuana',
  'America/Hermosillo',
  'America/Monterrey',
  'America/Cancun',
  'America/Mazatlan',
];

export function CondominioPagina() {
  const { datos, cargando, error, recargar } = useRecurso<Condominio>('/condominio');
  const [campos, setCampos] = useState<Partial<Condominio>>({});
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);

  useEffect(() => {
    if (datos) setCampos(datos);
  }, [datos]);

  function set<K extends keyof Condominio>(k: K, v: Condominio[K]) {
    setCampos((c) => ({ ...c, [k]: v }));
    setAviso(null);
  }

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setGuardando(true);
    setFallo(null);
    try {
      await pedir('/condominio', {
        metodo: 'PATCH',
        cuerpo: {
          nombre: campos.nombre,
          calle: campos.calle || null,
          ciudad: campos.ciudad || null,
          municipio: campos.municipio || null,
          pais: campos.pais || null,
          codigoPostal: campos.codigoPostal || null,
          zonaHoraria: campos.zonaHoraria,
          retencionFotosDias: campos.retencionFotosDias,
          exigirTarjetonSinQr: campos.exigirTarjetonSinQr,
          limiteVelocidadCamellon: campos.limiteVelocidadCamellon,
          limiteVelocidadCalles: campos.limiteVelocidadCalles,
        },
      });
      setAviso('Guardado.');
      recargar();
    } catch (err) {
      setFallo(err instanceof ErrorApi ? err.message : 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  }

  if (cargando) return <Cargando />;
  if (error) return <ErrorCaja>{error}</ErrorCaja>;

  return (
    <>
      <Encabezado
        titulo="Fraccionamiento"
        descripcion="Los datos del condominio y cuánto se conservan las fotos"
      />

      <form onSubmit={guardar} className="max-w-2xl space-y-stack-md">
        {fallo && <ErrorCaja>{fallo}</ErrorCaja>}

        <Panel>
          <div className="space-y-4 p-5">
            <Campo etiqueta="Nombre">
              <Entrada
                required
                value={campos.nombre ?? ''}
                onChange={(e) => set('nombre', e.target.value)}
              />
            </Campo>

            {/* Se enseña pero no se edita, y se dice por qué: quien administra
                lo necesita para configurar un aparato nuevo, y cambiarlo
                desconectaría las plumas y anularía las invitaciones vivas. */}
            <Campo
              etiqueta="Identificador técnico"
              ayuda="Va dentro de los códigos QR emitidos y de los tópicos de los aparatos. No se puede cambiar."
            >
              <Entrada value={campos.slug ?? ''} disabled />
            </Campo>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo etiqueta="Calle">
                <Entrada
                  value={campos.calle ?? ''}
                  onChange={(e) => set('calle', e.target.value)}
                />
              </Campo>
              <Campo etiqueta="Código postal">
                <Entrada
                  value={campos.codigoPostal ?? ''}
                  onChange={(e) => set('codigoPostal', e.target.value)}
                />
              </Campo>
              <Campo etiqueta="Ciudad">
                <Entrada
                  value={campos.ciudad ?? ''}
                  onChange={(e) => set('ciudad', e.target.value)}
                />
              </Campo>
              <Campo etiqueta="Municipio">
                <Entrada
                  value={campos.municipio ?? ''}
                  onChange={(e) => set('municipio', e.target.value)}
                />
              </Campo>
              <Campo etiqueta="País">
                <Entrada value={campos.pais ?? ''} onChange={(e) => set('pais', e.target.value)} />
              </Campo>
              <Campo
                etiqueta="Zona horaria"
                ayuda="Con este reloj se leen las vigencias de las invitaciones"
              >
                <Seleccion
                  value={campos.zonaHoraria ?? 'America/Mexico_City'}
                  onChange={(e) => set('zonaHoraria', e.target.value)}
                >
                  {ZONAS_HORARIAS.map((z) => (
                    <option key={z} value={z}>
                      {z}
                    </option>
                  ))}
                </Seleccion>
              </Campo>
            </div>
          </div>
        </Panel>

        <Panel>
          <div className="space-y-4 p-5">
            <h2 className="text-label-bold uppercase tracking-wider text-on-surface-variant">
              Conservación de fotos
            </h2>
            <p className="text-body-md text-on-surface-variant">
              La foto de una identificación lleva dentro la CURP, el domicilio y la firma de una
              persona. Pasado este plazo se borra sola; la visita sigue en la bitácora con su hora y
              su vivienda, que es lo que sostiene una aclaración.
            </p>
            <Campo etiqueta="Días que se guardan" ayuda="Entre 1 y 730">
              <Entrada
                type="number"
                min={1}
                max={730}
                value={campos.retencionFotosDias ?? 90}
                onChange={(e) => set('retencionFotosDias', Number(e.target.value))}
              />
            </Campo>
          </div>
        </Panel>

        <Panel>
          <div className="space-y-4 p-5">
            <h2 className="text-label-bold uppercase tracking-wider text-on-surface-variant">
              Límites de velocidad
            </h2>
            <p className="text-body-md text-on-surface-variant">
              Van dentro del mensaje que el residente le manda a su visita junto con el código. Son
              los de tu reglamento interno.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo etiqueta="Calles con camellón" ayuda="km/hr">
                <Entrada
                  type="number"
                  min={1}
                  max={120}
                  value={campos.limiteVelocidadCamellon ?? 30}
                  onChange={(e) => set('limiteVelocidadCamellon', Number(e.target.value))}
                />
              </Campo>
              <Campo etiqueta="Calles y privadas" ayuda="km/hr">
                <Entrada
                  type="number"
                  min={1}
                  max={120}
                  value={campos.limiteVelocidadCalles ?? 20}
                  onChange={(e) => set('limiteVelocidadCalles', Number(e.target.value))}
                />
              </Campo>
            </div>
          </div>
        </Panel>

        <Panel>
          <div className="space-y-4 p-5">
            <h2 className="text-label-bold uppercase tracking-wider text-on-surface-variant">
              Visitas sin código
            </h2>
            <p className="text-body-md text-on-surface-variant">
              Quien llega con un código QR ya viene identificado por el residente que se lo mandó.
              Quien llega sin código no trae nada de eso, y el tarjetón es lo que lo suple: es con
              lo que se sabe, si pasa algo adentro, de quién es el coche que no debería estar ahí.
            </p>
            <label className="toque flex items-start gap-3 text-body-md text-on-surface">
              <input
                type="checkbox"
                checked={campos.exigirTarjetonSinQr ?? true}
                onChange={(e) => set('exigirTarjetonSinQr', e.target.checked)}
                className="mt-0.5 h-5 w-5"
              />
              <span>
                Pedir tarjetón para dejar entrar una visita sin código
                <span className="block text-body-md text-on-surface-variant">
                  Apágalo solo si este fraccionamiento no reparte tarjetones: con la casilla vacía,
                  el guardia puede dejar pasar sin asignar ninguno.
                </span>
              </span>
            </label>
          </div>
        </Panel>

        <div className="flex items-center gap-4">
          <Boton type="submit" variante="primario" disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </Boton>
          {aviso && <span className="text-body-md text-on-surface-variant">{aviso}</span>}
        </div>
      </form>
    </>
  );
}
